<template>
  <transition name="modal-fade">
    <div v-if="visible" class="cmodal-overlay" @click.self="close">
      <div class="cmodal-box" role="dialog" aria-modal="true">
        <!-- Header -->
        <div class="cmodal-header">
          <div class="cmodal-header-left">
            <font-awesome-icon icon="fa-solid fa-paper-plane" class="cmodal-header-icon" />
            <span>{{ $t('contact_modal_title') }}</span>
          </div>
          <button class="cmodal-close" @click="close" :aria-label="$t('contact_modal_close')">
            <font-awesome-icon icon="fa-solid fa-xmark" />
          </button>
        </div>

        <!-- Body -->
        <div class="cmodal-body">
          <!-- Done state -->
          <div v-if="step === 'done'" class="cmodal-done">
            <font-awesome-icon icon="fa-solid fa-circle-check" class="cmodal-done-icon" />
            <h3>{{ $t('contact_modal_done_title') }}</h3>
            <p>{{ $t('contact_modal_done_body') }}</p>
          </div>

          <!-- Form -->
          <form v-else @submit.prevent="submitForm" novalidate>
            <!-- Dropdown: problem -->
            <div class="cform-group">
              <label class="cform-label">
                {{ $t('contact_modal_field_problem') }}
                <span class="cform-required">*</span>
              </label>
              <div class="cform-select-wrap">
                <select v-model="form.problem" class="cform-select" :class="{ 'cform-error-border': errors.problem }">
                  <option value="" disabled>{{ $t('contact_modal_problem_placeholder') }}</option>
                  <option v-for="p in problems" :key="p.key" :value="p.label">{{ p.label }}</option>
                </select>
                <font-awesome-icon icon="fa-solid fa-chevron-down" class="cform-select-icon" />
              </div>
              <p v-if="errors.problem" class="cform-error-msg">{{ errors.problem }}</p>
            </div>

            <!-- Textarea: detail -->
            <div class="cform-group">
              <label class="cform-label">
                {{ $t('contact_modal_field_detail') }}
                <span class="cform-optional">({{ $t('contact_modal_optional') }})</span>
              </label>
              <textarea
                v-model="form.detail"
                class="cform-textarea"
                :class="{ 'cform-error-border': errors.detail }"
                :placeholder="$t('contact_modal_detail_placeholder')"
                rows="4"
                maxlength="500"
                @input="validateDetail"
              ></textarea>
              <div class="cform-char-count">{{ form.detail.length }}/500</div>
              <p v-if="errors.detail" class="cform-error-msg">{{ errors.detail }}</p>
            </div>

            <!-- Name -->
            <div class="cform-group">
              <label class="cform-label">
                {{ $t('contact_modal_field_name') }}
                <span class="cform-required">*</span>
              </label>
              <input
                v-model="form.name"
                type="text"
                class="cform-input"
                :class="{ 'cform-error-border': errors.name }"
                :placeholder="$t('chat_input_name_placeholder')"
                maxlength="60"
                @input="validateName"
              />
              <p v-if="errors.name" class="cform-error-msg">{{ errors.name }}</p>
            </div>

            <!-- Email -->
            <div class="cform-group">
              <label class="cform-label">
                {{ $t('contact_modal_field_email') }}
                <span class="cform-required">*</span>
              </label>
              <input
                v-model="form.email"
                type="email"
                class="cform-input"
                :class="{ 'cform-error-border': errors.email }"
                :placeholder="$t('chat_input_email_placeholder')"
                maxlength="100"
                @input="validateEmail"
              />
              <p v-if="errors.email" class="cform-error-msg">{{ errors.email }}</p>
            </div>

            <!-- Phone -->
            <div class="cform-group">
              <label class="cform-label">
                {{ $t('contact_modal_field_phone') }}
                <span class="cform-required">*</span>
              </label>
              <input
                v-model="form.phone"
                type="tel"
                class="cform-input"
                :class="{ 'cform-error-border': errors.phone }"
                :placeholder="$t('chat_input_phone_placeholder')"
                maxlength="15"
                @input="validatePhone"
              />
              <p v-if="errors.phone" class="cform-error-msg">{{ errors.phone }}</p>
            </div>

            <!-- Honeypot -->
            <input
              v-model="honeypot"
              name="website"
              type="text"
              tabindex="-1"
              autocomplete="off"
              class="cform-honeypot"
            />

            <!-- Submit error -->
            <p v-if="errors.submit" class="cform-error-msg cform-submit-error">{{ errors.submit }}</p>

            <!-- Submit button -->
            <button type="submit" class="cform-submit" :disabled="isSending || !isFormValid">
              <span v-if="isSending">
                <font-awesome-icon icon="fa-solid fa-spinner" spin /> {{ $t('chat_sending') }}
              </span>
              <span v-else>
                <font-awesome-icon icon="fa-solid fa-paper-plane" /> {{ $t('chat_submit') }}
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import emailjs from '@emailjs/browser';

const BLOCKED_PATTERNS = [
  /https?:\/\//i,
  /www\./i,
  /<[^>]*>/,
  /javascript:/i,
  /on\w+\s*=/i,
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/,
];

const RATE_LIMIT_MS = 60 * 1000;

function containsBlocked(value) {
  return BLOCKED_PATTERNS.some((p) => p.test(value));
}

export default {
  name: 'ContactModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step: 'form',
      honeypot: '',
      isSending: false,
      formOpenTime: null,
      form: {
        problem: '',
        detail: '',
        name: '',
        email: '',
        phone: '',
      },
      errors: {
        problem: '',
        detail: '',
        name: '',
        email: '',
        phone: '',
        submit: '',
      },
    };
  },
  computed: {
    problems() {
      return [
        { key: 'p1', label: this.$t('chat_problem_1') },
        { key: 'p2', label: this.$t('chat_problem_2') },
        { key: 'p3', label: this.$t('chat_problem_3') },
        { key: 'p4', label: this.$t('chat_problem_4') },
        { key: 'p5', label: this.$t('chat_problem_5') },
        { key: 'other', label: this.$t('chat_problem_other') },
      ];
    },
    isFormValid() {
      return (
        this.form.problem &&
        this.form.name.trim() &&
        this.form.email.trim() &&
        this.form.phone.trim() &&
        !this.errors.name &&
        !this.errors.email &&
        !this.errors.phone &&
        !this.errors.detail
      );
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.formOpenTime = Date.now();
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
        this.resetForm();
      }
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    resetForm() {
      this.step = 'form';
      this.honeypot = '';
      this.isSending = false;
      this.form = { problem: '', detail: '', name: '', email: '', phone: '' };
      this.errors = { problem: '', detail: '', name: '', email: '', phone: '', submit: '' };
    },
    validateDetail() {
      this.errors.detail = containsBlocked(this.form.detail) ? this.$t('chat_error_invalid') : '';
    },
    validateName() {
      this.errors.name = containsBlocked(this.form.name) ? this.$t('chat_error_invalid') : '';
    },
    validateEmail() {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(this.form.email.trim())) {
        this.errors.email = this.$t('chat_error_email');
      } else if (containsBlocked(this.form.email)) {
        this.errors.email = this.$t('chat_error_invalid');
      } else {
        this.errors.email = '';
      }
    },
    validatePhone() {
      const phonePattern = /^[0-9+()\s-]{8,15}$/;
      this.errors.phone = !phonePattern.test(this.form.phone.trim()) ? this.$t('chat_error_phone') : '';
    },
    canSubmit() {
      const last = localStorage.getItem('contact_last_submit');
      return !(last && Date.now() - parseInt(last) < RATE_LIMIT_MS);
    },
    async submitForm() {
      // Validate all fields
      this.errors.problem = !this.form.problem ? this.$t('contact_modal_error_required') : '';
      this.validateName();
      this.validateEmail();
      this.validatePhone();
      this.validateDetail();

      if (!this.isFormValid || this.errors.problem) return;

      // Honeypot
      if (this.honeypot !== '') { this.step = 'done'; return; }

      // Rate limit
      if (!this.canSubmit()) {
        this.errors.submit = this.$t('chat_error_rate_limit');
        return;
      }

      // Time check
      if (this.formOpenTime && Date.now() - this.formOpenTime < 3000) {
        this.step = 'done';
        return;
      }

      this.isSending = true;
      this.errors.submit = '';

      try {
        await emailjs.send(
          process.env.VUE_APP_EMAILJS_SERVICE_ID,
          process.env.VUE_APP_EMAILJS_TEMPLATE_ID,
          {
            problem: this.form.problem,
            name: this.form.name,
            email: this.form.email,
            phone: this.form.phone,
            detail: this.form.detail || '—',
            time: new Date().toLocaleString('ja-JP'),
          },
          process.env.VUE_APP_EMAILJS_PUBLIC_KEY
        );
        localStorage.setItem('contact_last_submit', Date.now().toString());
        this.step = 'done';
      } catch {
        this.errors.submit = this.$t('chat_error_send_failed');
      } finally {
        this.isSending = false;
      }
    },
  },
};
</script>

<style scoped>
/* Overlay */
.cmodal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

/* Box */
.cmodal-box {
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

/* Header */
.cmodal-header {
  background: linear-gradient(135deg, #111c55 0%, #1e3a8a 100%);
  color: #fff;
  padding: 18px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}
.cmodal-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.03em;
}
.cmodal-header-icon {
  font-size: 1.1rem;
}
.cmodal-close {
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  font-size: 1rem;
  cursor: pointer;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}
.cmodal-close:hover {
  background: rgba(255,255,255,0.3);
}

/* Body */
.cmodal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  text-align: left;
}

/* Form groups */
.cform-group {
  margin-bottom: 18px;
}
.cform-label {
  display: block;
  font-size: 0.88rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}
.cform-required {
  color: #e53e3e;
  margin-left: 3px;
}
.cform-optional {
  color: #999;
  font-weight: 400;
  font-size: 0.8rem;
  margin-left: 4px;
}

/* Select */
.cform-select-wrap {
  position: relative;
}
.cform-select {
  width: 100%;
  appearance: none;
  -webkit-appearance: none;
  border: 1.5px solid #c2caef;
  border-radius: 10px;
  padding: 11px 40px 11px 14px;
  font-size: 0.9rem;
  color: #333;
  background: #fff;
  outline: none;
  cursor: pointer;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.cform-select:focus {
  border-color: #111c55;
}
.cform-select-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  font-size: 0.8rem;
  pointer-events: none;
}

/* Input & Textarea */
.cform-input,
.cform-textarea {
  width: 100%;
  border: 1.5px solid #c2caef;
  border-radius: 10px;
  padding: 11px 14px;
  font-size: 0.9rem;
  color: #333;
  outline: none;
  resize: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
  font-family: inherit;
}
.cform-input:focus,
.cform-textarea:focus {
  border-color: #111c55;
}
.cform-error-border {
  border-color: #e53e3e !important;
}

/* Char count */
.cform-char-count {
  text-align: right;
  font-size: 0.75rem;
  color: #aaa;
  margin-top: 4px;
}

/* Error messages */
.cform-error-msg {
  color: #e53e3e;
  font-size: 0.8rem;
  margin: 4px 0 0;
}
.cform-submit-error {
  text-align: center;
  margin-bottom: 8px;
}

/* Honeypot */
.cform-honeypot {
  position: absolute;
  left: -9999px;
  opacity: 0;
  height: 0;
  width: 0;
  pointer-events: none;
}

/* Submit button */
.cform-submit {
  width: 100%;
  background: linear-gradient(135deg, #111c55 0%, #1e3a8a 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 14px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
}
.cform-submit:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}
.cform-submit:disabled {
  background: #aaa;
  cursor: not-allowed;
  transform: none;
}

/* Done screen */
.cmodal-done {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
  gap: 16px;
}
.cmodal-done-icon {
  font-size: 3.5rem;
  color: #27ae60;
}
.cmodal-done h3 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #111c55;
  margin: 0;
}
.cmodal-done p {
  color: #555;
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.6;
}

/* Transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}
.modal-fade-enter-active .cmodal-box,
.modal-fade-leave-active .cmodal-box {
  transition: transform 0.25s ease;
}
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter .cmodal-box {
  transform: scale(0.95) translateY(20px);
}

/* Mobile */
@media (max-width: 576px) {
  .cmodal-box {
    border-radius: 16px 16px 0 0;
    max-height: 95vh;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
  }
  .cmodal-overlay {
    align-items: flex-end;
    padding: 0;
  }
}
</style>
